import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity'
import { Action, createReducer, on } from '@ngrx/store'

import { initialDisplayColumns } from 'libs/gb-dashboard/event-tickets/feature/src/lib/gb-event-ticket-purchase-list/gb-event-ticket-purchase-list-display-columns'
import {
  clearSearchHistoryAction,
  getAddSearchHistoryAction,
} from 'libs/gb-dashboard/shared/data-access-search-history/src/lib/+state/search-history.actions'
import { GridSwitchEnum } from 'libs/gb-dashboard/shared/feature/src/lib/gb-grid-table-switch/gb-grid-table-switch.component'
import { GbTableColumn } from 'libs/gb-dashboard/shared/feature/src/lib/gb-table/gb-table.models'
import * as EventTicketPurchasesActions from './event-ticket-purchases.actions'
import { EventTicketPurchasesEntity, ticketPuchasesEntityName } from './event-ticket-purchases.models'

export const EVENT_TICKET_PURCHASES_FEATURE_KEY = 'eventTicketPurchases'

export interface EventTicketPurchasesState extends EntityState<EventTicketPurchasesEntity> {
  selectedId?: string
  loaded: boolean
  searchHistory: string[]
  count: number
  dataView: GridSwitchEnum
  displayColumns: GbTableColumn[]
  searchTerm?: string
  order?: string
  refunded?: boolean
  placePurchaseLoaded: boolean
}

export interface EventTicketPurchasesPartialState {
  readonly [EVENT_TICKET_PURCHASES_FEATURE_KEY]: EventTicketPurchasesState
}

export const eventTicketPurchasesAdapter: EntityAdapter<EventTicketPurchasesEntity> =
  createEntityAdapter<EventTicketPurchasesEntity>({
    selectId: (eventTicketPurchase: EventTicketPurchasesEntity) => eventTicketPurchase.uuid,
  })

export const initialEventTicketPurchasesState: EventTicketPurchasesState = eventTicketPurchasesAdapter.getInitialState({
  searchHistory: [],
  loaded: false,
  count: 0,
  dataView: GridSwitchEnum.Table,
  displayColumns: initialDisplayColumns,
  placePurchaseLoaded: true,
})

const eventTicketPurchasesReducer = createReducer(
  initialEventTicketPurchasesState,
  on(EventTicketPurchasesActions.loadEventTicketPurchases, (state, action) => ({
    ...state,
    searchTerm: action.searchTerm,
    order: action.order,
    refunded: action.refunded,
    loaded: false,
  })),
  on(EventTicketPurchasesActions.setEventTicketPurchases, (state, action) =>
    eventTicketPurchasesAdapter.setAll(action.eventTicketPurchases, { ...state, loaded: true, count: action.count }),
  ),
  on(EventTicketPurchasesActions.loadEventTicketPurchasesSuccess, (state, action) =>
    eventTicketPurchasesAdapter.upsertMany(action.eventTicketPurchases, {
      ...state,
      loaded: true,
      count: action.count,
    }),
  ),
  on(EventTicketPurchasesActions.loadEventTicketPurchasesFailure, (state) => ({
    ...state,
    loaded: true,
  })),
  on(EventTicketPurchasesActions.displayGrid, (state) => ({
    ...state,
    dataView: GridSwitchEnum.Grid,
  })),
  on(EventTicketPurchasesActions.displayTable, (state) => ({
    ...state,
    dataView: GridSwitchEnum.Table,
  })),
  on(EventTicketPurchasesActions.updateDisplayColumns, (state, action) => ({
    ...state,
    displayColumns: action.displayColumns,
  })),
  on(EventTicketPurchasesActions.placeEventTicketPurchaseSuccess, (state, action) => {
    const newState = eventTicketPurchasesAdapter.addOne(action.eventTicketPurchase, state)
    return { ...newState, selectedId: action.eventTicketPurchase.uuid }
  }),
  on(EventTicketPurchasesActions.syncEventTicketPurchaseSuccess, (state, action) =>
    eventTicketPurchasesAdapter.updateOne(action.update, state),
  ),
  on(EventTicketPurchasesActions.clearSelectedEventTicketPurchase, (state) => ({
    ...(state.selectedId ? eventTicketPurchasesAdapter.removeOne(state.selectedId, state) : state),
    selectedId: initialEventTicketPurchasesState.selectedId,
  })),

  on(getAddSearchHistoryAction(ticketPuchasesEntityName), (state, action) => ({
    ...state,
    searchHistory: [...state.searchHistory, action.searchTerm],
  })),
  on(clearSearchHistoryAction(ticketPuchasesEntityName), (state, action) => ({
    ...state,
    searchHistory: initialEventTicketPurchasesState.searchHistory,
  })),
  on(EventTicketPurchasesActions.placeEventTicketPurchase, (state) => ({
    ...state,
    placePurchaseLoaded: false,
  })),
  on(
    EventTicketPurchasesActions.placeEventTicketPurchaseSuccess,
    EventTicketPurchasesActions.placeEventTicketPurchaseFailure,
    (state) => ({
      ...state,
      placePurchaseLoaded: true,
    }),
  ),
  on(EventTicketPurchasesActions.clearSelectedEventTicketPurchaseId, (state, action) => ({
    ...state,
    selectedId: initialEventTicketPurchasesState.selectedId,
  })),
)

export function reducer(state: EventTicketPurchasesState | undefined, action: Action) {
  return eventTicketPurchasesReducer(state, action)
}
